/**
 * Shared API fetch wrapper for the dashboard.
 *
 * Handles a few cases that every per-domain client (`assistant.ts`,
 * `status.ts`, etc.) was reimplementing inconsistently:
 *
 *   - JSON Content-Type default + credentials: 'include'
 *   - 204 No Content → returns `null`
 *   - 4xx/5xx → throws `Error` with the server-provided `error` string when
 *     the body is JSON, falling back to `statusText`
 *   - 503 with body `{ error: "feature_disabled", feature: "<name>" }` →
 *     throws a typed `FeatureDisabledError`. Callers (or React Query queryFn
 *     wrappers) can catch this distinctly to render an empty/disabled state
 *     instead of a generic error toast or infinite retry.
 *
 * The tunnel-proxy nginx config returns the feature_disabled body for
 * intentionally-disabled services (ss-api, meeting-agent, design-service,
 * comms-service, sop-service). Those endpoints will keep returning 503 in
 * the network tab; this wrapper just stops the UI from treating them as a
 * crash.
 */

export class FeatureDisabledError extends Error {
  readonly feature: string;

  constructor(feature: string) {
    super(`feature_disabled:${feature}`);
    this.name = 'FeatureDisabledError';
    this.feature = feature;
  }
}

export function isFeatureDisabledError(
  err: unknown,
): err is FeatureDisabledError {
  return err instanceof FeatureDisabledError;
}

/**
 * Inspects a `Response` and throws `FeatureDisabledError` if it carries the
 * nginx `feature_disabled` payload. Safe to call after any `await fetch(...)`
 * before the component reads the response — uses `response.clone()` so the
 * caller can still consume the body normally on success.
 *
 * Use in components that fetch directly (i.e., not through the typed clients
 * in `src/api/*.ts`) so they degrade to an empty state instead of treating a
 * disabled service as a generic crash.
 */
type FeatureDisabledBody = { error?: string; feature?: string };

export async function throwIfFeatureDisabled(response: Response): Promise<void> {
  if (response.status !== 503) return;
  let body: FeatureDisabledBody | null = null;
  try {
    body = (await response.clone().json()) as FeatureDisabledBody;
  } catch {
    try {
      body = JSON.parse(await response.clone().text()) as FeatureDisabledBody;
    } catch {
      body = null;
    }
  }
  if (body?.error === 'feature_disabled') {
    throw new FeatureDisabledError(body.feature ?? 'unknown');
  }
}

/** Returns true for any client/server error response. */
function isJsonResponse(res: Response): boolean {
  return (res.headers.get('content-type') ?? '').toLowerCase().includes('json');
}

export async function apiFetch<T>(
  url: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(url, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  });

  // 503 with `feature_disabled` payload → typed error so the UI can degrade.
  if (res.status === 503) {
    let body: FeatureDisabledBody | null = null;
    try {
      // .clone() so a downstream caller could still read res if it wanted to.
      body = (await res.clone().json()) as FeatureDisabledBody;
    } catch {
      // Some nginx variants return the body as text/plain even though it's
      // JSON-shaped. Try once more by parsing the raw text.
      try {
        const text = await res.clone().text();
        body = JSON.parse(text) as FeatureDisabledBody;
      } catch {
        body = null;
      }
    }
    if (body?.error === 'feature_disabled') {
      throw new FeatureDisabledError(body.feature ?? 'unknown');
    }
  }

  if (!res.ok) {
    let errorMessage = res.statusText;
    if (isJsonResponse(res)) {
      const body = (await res.json().catch(() => null)) as {
        error?: string;
      } | null;
      if (body?.error) errorMessage = body.error;
    }
    throw new Error(errorMessage);
  }

  if (res.status === 204) return null as T;
  return res.json();
}
